import React from 'react';

import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link, { LinkProps } from '@mui/material/Link';
import { Link as RouterLink, useLocation, useMatches, useParams } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import { useAppSelector } from '../../redux/store';
import { useTranslation } from 'react-i18next';

interface LinkRouterProps extends LinkProps {
	to: string;
	replace?: boolean;
	state?: { [key: string]: any };
}

interface RouteHandle {
	key: string;
	showDocumentFileName?: boolean;
	showCategoryName?: boolean;
	showStudentName?: boolean;
	handler?: () => string;
}

function LinkRouter(props: LinkRouterProps) {
	return <Link {...props} component={RouterLink} />;
}

const NavBreadcrumbs = () => {
	const matches = useMatches();
	const currentDocument = useAppSelector((state) => state.document.currentDocument);
	const walletCategories = useAppSelector(
		(state) => state.walletCategories.proxyCategories ?? state.walletCategories.categories
	);
	const { t } = useTranslation();
	const { categoryId } = useParams();
	const { state } = useLocation();

	const getDocumentFileName = () => {
		return currentDocument?.title;
	};

	const items = matches
		.filter((m) => m.handle)
		.map((match) => {
			const handle = match.handle as RouteHandle;
			if (handle.showDocumentFileName) {
				return {
					text: getDocumentFileName() ?? '',
					href: match.pathname
				};
			}
			if (handle.showCategoryName) {
				const categoryLabel =
					walletCategories.find((c) => c.id === categoryId)?.label ||
					t('pages.wallet.categories.' + categoryId, { defaultValue: categoryId });
				return {
					text: categoryLabel,
					href: match.pathname
				};
			}
			return {
				text: handle.handler ? handle.handler() : t(handle.key),
				href: match.pathname
			};
		});

	return (
		<Breadcrumbs aria-label="breadcrumb" id="breadcrumb">
			{items.map((value, index) => {
				const last = index === items.length - 1;

				return last ? (
					<Typography key={value.href} fontSize="14px">
						{value.text}
					</Typography>
				) : (
					<LinkRouter
						underline="hover"
						color="inherit"
						fontSize="14px"
						to={value.href}
						key={value.href}
						state={state}
					>
						{value.text}
					</LinkRouter>
				);
			})}
		</Breadcrumbs>
	);
};

export default NavBreadcrumbs;
